import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { contacts } from './data';
import { ContactCard } from '../../../components/crm/contacts/contact-card';
import { useNavigate } from 'react-router-dom';

const CrmContacts: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<'all' | 'customers' | 'prospects'>('all');

const navigate = useNavigate();

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.title.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesLifecycle =
      lifecycleFilter === 'all' ||
      (lifecycleFilter === 'customers' && contact.status === 'Customer') ||
      (lifecycleFilter === 'prospects' && contact.status === 'Prospect');

    return matchesSearch && matchesLifecycle;
  });

  const customerCount = contacts.filter(c => c.status === 'Customer').length;
  const prospectCount = contacts.filter(c => c.status === 'Prospect').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 md:w-10 md:h-10 text-blue-600" />
            <h1 className="text-2xl md:text-3xl lg:text-2xl font-bold text-gray-900">Contacts</h1>
          </div>
          <p className="text-sm md:text-base text-gray-600">
            All contacts and customers in your CRM with quick actions
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Filters</h2>
          <div className="flex flex-col lg:flex-row gap-4">
            <input
              type="text"
              placeholder="Search name, company, email, phone, title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />

            <div className="flex gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700 flex items-center">
                Lifecycle:
              </span>
              <button
                onClick={() => setLifecycleFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  lifecycleFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({contacts.length})
              </button>
              <button
                onClick={() => setLifecycleFilter('customers')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  lifecycleFilter === 'customers'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Customers ({customerCount})
              </button>
              <button
                onClick={() => setLifecycleFilter('prospects')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  lifecycleFilter === 'prospects'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Prospects ({prospectCount})
              </button>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Showing {filteredContacts.length} of {contacts.length} contacts
          </p>
        </div>

        <div className="h-[600px] bg-white shadow rounded-xl overflow-y-auto p-7">
          {filteredContacts.map(contact => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onClick={() => navigate('/contact/details')}
              onInboxClick={(e) => {
                e.stopPropagation();
                navigate('#');
              }}
              onActivityClick={(e) => {
                e.stopPropagation();
                navigate('/activities');
              }}
            />
          ))}

          {filteredContacts.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No contacts found</h3>
              <p className="text-gray-600">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrmContacts;